import { useEffect, useRef, useState } from 'react';

import { adminApi, ResumePdfAuditProgress } from '@/api/admin.api';

const POLL_INTERVAL_MS = 2000;

const pct = (part: number, whole: number) =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—';

export function ResumePdfAudit() {
  const [activeJob, setActiveJob] = useState<ResumePdfAuditProgress | null>(null);
  const [recentJobs, setRecentJobs] = useState<ResumePdfAuditProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = async () => {
    try {
      const [{ active }, { jobs }] = await Promise.all([
        adminApi.getActiveResumePdfAudit(),
        adminApi.getRecentResumePdfAudits(),
      ]);
      setActiveJob(active);
      setRecentJobs(jobs);
      return active;
    } catch (e: any) {
      setError(e.message || 'Failed to load audit status');
      return null;
    }
  };

  useEffect(() => {
    refresh();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a scan is running
  useEffect(() => {
    if (activeJob?.status === 'RUNNING' && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const active = await refresh();
        if (active?.status !== 'RUNNING' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, POLL_INTERVAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.status]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      await adminApi.startResumePdfAudit();
      await refresh();
    } catch (e: any) {
      setError(e.message || 'Failed to start audit');
    } finally {
      setStarting(false);
    }
  };

  const running = activeJob?.status === 'RUNNING';
  const latest = activeJob ?? recentJobs[0] ?? null;

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">Resume PDF Audit</h1>
      <p className="text-sm text-gray-500 mb-6">
        Candidate resumes are shown to recruiters by reading a rendered PDF out of S3. When that
        file is missing, the app falls back to a single-page screenshot — so the recruiter sees a
        cropped resume. This scan pages every document in <code>resumes_v2</code> and checks
        whether the PDF actually exists, to size how many candidates are currently affected.
        Read-only: nothing is written to S3 or Elasticsearch, and it is safe to run in production.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="mb-8">
        <button
          onClick={start}
          disabled={running || starting}
          className="px-4 py-2 rounded bg-slate-900 text-white text-sm font-medium disabled:opacity-50"
        >
          {running ? 'Scan in progress…' : starting ? 'Starting…' : 'Run audit'}
        </button>
      </div>

      {latest && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {activeJob ? 'Current scan' : 'Last scan'}
          </h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200 border-b border-gray-200">
              <Stat label="Checked" value={`${latest.checkedDocs} / ${latest.totalDocs}`} />
              <Stat label="Has PDF" value={String(latest.withPdf)} />
              <Stat
                label="Missing PDF"
                value={String(latest.missingPdf)}
                tone={latest.missingPdf > 0 ? 'warn' : 'ok'}
                sub={pct(latest.missingPdf, latest.checkedDocs)}
              />
              <Stat label="Uncheckable" value={String(latest.skippedDocs)} sub="no userId" />
            </div>
            <div className="px-4 py-3 text-xs text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
              <span>
                Status: <span className="font-medium text-gray-700">{latest.status}</span>
              </span>
              <span>Started: {new Date(latest.startedAt).toLocaleString()}</span>
              {latest.finishedAt && (
                <span>Finished: {new Date(latest.finishedAt).toLocaleString()}</span>
              )}
              <span className="font-mono">{latest.jobId}</span>
            </div>
            {latest.error && (
              <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border-t border-red-200">
                {latest.error}
              </div>
            )}
          </div>
        </div>
      )}

      {latest && latest.sampleMissing.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Affected candidates{' '}
            <span className="font-normal normal-case text-gray-500">
              (first {latest.sampleMissing.length} of {latest.missingPdf})
            </span>
          </h2>
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Resume ID</th>
                  <th className="px-4 py-2 font-medium">User ID</th>
                  <th className="px-4 py-2 font-medium">Title</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {latest.sampleMissing.map((sample) => (
                  <tr key={sample.resumeId}>
                    <td className="px-4 py-2 font-mono text-xs">{sample.resumeId}</td>
                    <td className="px-4 py-2 font-mono text-xs">{sample.userId}</td>
                    <td className="px-4 py-2">{sample.title || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recentJobs.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Recent scans
          </h2>
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Started</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Checked</th>
                  <th className="px-4 py-2 font-medium">Missing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentJobs.map((job) => (
                  <tr key={job.jobId}>
                    <td className="px-4 py-2">{new Date(job.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2">{job.status}</td>
                    <td className="px-4 py-2">{job.checkedDocs}</td>
                    <td className="px-4 py-2">
                      {job.missingPdf} ({pct(job.missingPdf, job.checkedDocs)})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'ok' | 'warn';
}) {
  const toneClass =
    tone === 'warn' ? 'text-amber-700' : tone === 'ok' ? 'text-emerald-700' : 'text-gray-900';
  return (
    <div className="px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}
